// src/pages/Admin/TagEditorPage.js
import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import EditTagsTab from "./EditTagsTab.js";
import EditTagsCategories from "./EditTagsCategories";
import axios from "axios";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import useAccess from "../../hooks/useAccess";
import "../../styles/Form.css";
import "../../styles/TagEditorPage.css";

const TagEditorPage = ({
  userId: userIdProp,
  hideAssignTab = false,
  onTagsSaved,
}) => {
  const params = useParams();
  const userId = userIdProp ?? params?.userId;

  const [allTags, setAllTags] = useState([]);
  const [assignedTags, setAssignedTags] = useState([]);
  const [assignedValues, setAssignedValues] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [activeTab, setActiveTab] = useState(hideAssignTab ? "edit" : "assign");
  const [categoryList, setCategoryList] = useState([]);
  const [tagCategories, setTagCategories] = useState({});
  const [editedNames, setEditedNames] = useState({});
  const [pendingNewTags, setPendingNewTags] = useState([]);
  const [pendingDeletes, setPendingDeletes] = useState([]);
  const [editTagId, setEditTagId] = useState(null);

  const { hasPermission } = useAccess();

  // ⭐ NEW: Centralized PERMISSION Map
  const PERMISSIONS = {
    tags: {
      assign: hasPermission("AccountSetting", "Tags", "edit"),
      create: hasPermission("AccountSetting", "Tags", "edit"),
      edit: hasPermission("AccountSetting", "Tags", "edit"),
    },
  };

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const tagPromise = axios.get(`${BASE_URL}/api/dropdowns/tags`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const categoryPromise = axios.get(
        `${BASE_URL}/api/dropdowns/tag-categories`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const promises = [tagPromise, categoryPromise];

      if (userId) {
        const userTagsPromise = axios.get(
          `${BASE_URL}/api/userTags/users/${userId}/tags`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        promises.push(userTagsPromise);
      }

      const [tagsRes, categoryRes, userTagsRes] = await Promise.all(promises);

      setAllTags(tagsRes.data || []);
      const categoriesByTagId = {};
      (tagsRes.data || []).forEach((tag) => {
        categoriesByTagId[tag.t_id] = tag.t_categories || "";
      });
      setTagCategories(categoriesByTagId);
      setCategoryList(categoryRes.data || []);

      if (userId && userTagsRes) {
        setAssignedTags(userTagsRes.data.tags || []);
        setAssignedValues(userTagsRes.data.tagsWithValue || {});
      }
    } catch (err) {
      console.error("❌ Error loading tag data:", err);
    }
  }, [BASE_URL, userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleTag = (id) => {
    if (assignedTags.includes(id)) {
      const updated = assignedTags.filter((t) => t !== id);
      const valuesCopy = { ...assignedValues };
      delete valuesCopy[id];
      setAssignedTags(updated);
      setAssignedValues(valuesCopy);
    } else {
      setAssignedTags([...assignedTags, id]);
    }
  };

  const handleNewTag = async () => {
    if (!newTag.trim()) return;
    try {
      const res = await axios.post(
        `${BASE_URL}/api/userTags/tags`,
        { tagName: newTag },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      setAllTags([...allTags, res.data]);
      setNewTag("");
    } catch (err) {
      console.error("❌ Error creating tag:", err);
    }
  };

  const handleSaveTags = async () => {
    try {
      await axios.put(
        `${BASE_URL}/api/userTags/users/${userId}/tags`,
        { tagIds: assignedTags, tagValues: assignedValues },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      await fetchData();
      await onTagsSaved?.();
      alert("User tags updated.");
    } catch (err) {
      console.error("❌ Error saving tags:", err);
    }
  };

  const handleSaveEditedTags = async () => {
    const token = localStorage.getItem("token");
    const newTagEntries = Object.entries(editedNames).filter(
      ([id]) => id >= 1e12
    );

    for (const [, name] of newTagEntries) {
      try {
        await axios.post(
          `${BASE_URL}/api/userTags/tags`,
          { tagName: name },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        console.error("❌ Error saving new tag:", err);
      }
    }

    const renamePromises = allTags
      .map((tag) => {
        const edited = editedNames[tag.t_id];
        if (edited && edited !== tag.t_name) {
          return axios.put(
            `${BASE_URL}/api/userTags/tags/${tag.t_id}`,
            { newName: edited },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
        return null;
      })
      .filter(Boolean);

    const categoryPromises = allTags
      .filter((tag) => {
        const next = tagCategories[tag.t_id];
        const prev = tag.t_categories ?? "";
        const normalizedNext = next === "" || isNaN(next) ? "" : next;
        return normalizedNext !== prev;
      })
      .map((tag) => {
        const next = tagCategories[tag.t_id];
        const payload = {
          t_categories: next === "" || isNaN(next) ? null : next,
        };
        return axios.put(`${BASE_URL}/api/userTags/tags/${tag.t_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      });

    const deletePromises = pendingDeletes.map((id) =>
      axios.delete(`${BASE_URL}/api/userTags/tags/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
    );

    try {
      await Promise.all([
        ...renamePromises,
        ...categoryPromises,
        ...deletePromises,
      ]);
      await fetchData();
      alert("Saved successfully!");
    } catch (err) {
      console.error("❌ Error saving changes:", err);
      alert("Save failed. Check console.");
    }

    setPendingNewTags([]);
    setPendingDeletes([]);
    setEditedNames({});
    setEditTagId(null);
  };

  const handleSaveTagCategoriesOnly = async () => {
    try {
      const token = localStorage.getItem("token");

      // 1. Save new categories to DB
      const newCategories = categoryList.filter((cat) =>
        String(cat.tc_id).startsWith("new-")
      );

      const createdCategoryMap = {};

      for (const newCat of newCategories) {
        const res = await axios.post(
          `${BASE_URL}/api/userTags/categories`,
          { name: newCat.tc_name },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const saved = res.data.newCategory;
        createdCategoryMap[newCat.tc_id] = saved.tc_id;
      }

      // 2. Remap temporary category IDs in tagCategories
      const updatedCatMap = { ...tagCategories };
      Object.entries(updatedCatMap).forEach(([tagId, catId]) => {
        if (createdCategoryMap[catId]) {
          updatedCatMap[tagId] = createdCategoryMap[catId];
        }
      });

      // 3. Save tag category updates
      await Promise.all(
        allTags.map((tag) => {
          const categoryId = updatedCatMap[tag.t_id] || null;
          return axios.put(
            `${BASE_URL}/api/userTags/tags/${tag.t_id}`,
            { t_categories: categoryId },
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
        })
      );

      // 4. Update frontend state with real IDs
      const refreshedCats = categoryList.map((cat) => {
        if (createdCategoryMap[cat.tc_id]) {
          return {
            ...cat,
            tc_id: createdCategoryMap[cat.tc_id],
          };
        }
        return cat;
      });

      setCategoryList(refreshedCats);
      setTagCategories(updatedCatMap);

      alert("✅ Tag categories updated successfully.");
    } catch (err) {
      console.error("❌ Error saving tag categories:", err);
      alert("Failed to save tag categories. Please check the console.");
    }
  };

  // ===== RENDER =====
  return (
    <>
      <div className="form-wrapper my-2">
        <div className="tabs">
          {!hideAssignTab && (
            <button
              className={activeTab === "assign" ? "active" : ""}
              onClick={() => setActiveTab("assign")}
            >
              ASSIGN TAGS
            </button>
          )}
          <button
            className={activeTab === "edit" ? "active" : ""}
            onClick={() => setActiveTab("edit")}
          >
            EDIT TAGS
          </button>
          <button
            className={activeTab === "editCat" ? "active" : ""}
            onClick={() => setActiveTab("editCat")}
          >
            EDIT TAG CATEGORIES
          </button>
        </div>

        {!hideAssignTab && activeTab === "assign" && (
          <div className="tag-editor-split">
            <div className="tag-section-left">
              <div className="section-header">
                <strong>Assigned Tags</strong>

                <label className="edit-toggle">
                  Edit
                  <input
                    type="checkbox"
                    checked={editMode}
                    onChange={() => setEditMode(!editMode)}
                    disabled={!PERMISSIONS.tags.assign}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="assigned-tags-list">
                {assignedTags.map((id) => {
                  const tag = allTags.find((t) => t.t_id === id);
                  return (
                    <div key={id} className="tag-pill">
                      <span>{tag?.t_name}</span>
                      {editMode ? (
                        <input
                          className="tag-value-input"
                          placeholder="Enter value"
                          value={assignedValues[id] || ""}
                          onChange={(e) =>
                            setAssignedValues({
                              ...assignedValues,
                              [id]: e.target.value,
                            })
                          }
                        />
                      ) : (
                        assignedValues[id] && (
                          <span> - {assignedValues[id]}</span>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="tag-section-right">
              <label>
                <strong>Add New Tag</strong>
              </label>
              <div className="add-tag-input-group">
                <input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Enter A New Tag Name Here"
                  disabled={!PERMISSIONS.tags.create}
                />
                <button
                  onClick={handleNewTag}
                  className="btn btn-light"
                  disabled={!PERMISSIONS.tags.create}
                >
                  Add
                </button>
              </div>

              <div className="tag-button-grid">
                {allTags.map((tag) => (
                  <button
                    key={tag.t_id}
                    className={`tag-btn ${
                      assignedTags.includes(tag.t_id) ? "assigned" : ""
                    }`}
                    onClick={() => handleToggleTag(tag.t_id)}
                    disabled={!PERMISSIONS.tags.assign}
                  >
                    {tag.t_name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === "edit" && (
          <EditTagsTab
            allTags={allTags}
            tagCategories={tagCategories}
            setTagCategories={setTagCategories}
            categoryList={categoryList}
            editedNames={editedNames}
            setEditedNames={setEditedNames}
            newTag={newTag}
            setNewTag={setNewTag}
            pendingNewTags={pendingNewTags}
            setPendingNewTags={setPendingNewTags}
            pendingDeletes={pendingDeletes}
            setPendingDeletes={setPendingDeletes}
            editTagId={editTagId}
            setEditTagId={setEditTagId}
          />
        )}

        {activeTab === "editCat" && (
          <DndProvider backend={HTML5Backend}>
            <EditTagsCategories
              allTags={allTags}
              categoryList={categoryList}
              tagCategories={tagCategories}
              setTagCategories={setTagCategories}
              setCategoryList={setCategoryList}
            />
          </DndProvider>
        )}

        <div className="footer-actions">
          {/* Save for Assign Permission */}
          {activeTab === "assign" && (
            <button
              className="btn btn-primary"
              onClick={handleSaveTags}
              disabled={!PERMISSIONS.tags.assign}
            >
              Save Assign
            </button>
          )}

          {/* Save for Edit Permission */}
          {(activeTab === "edit" || activeTab === "editCat") && (
            <button
              className="btn btn-success"
              onClick={() => {
                if (activeTab === "edit") handleSaveEditedTags();
                else handleSaveTagCategoriesOnly();
              }}
              disabled={!PERMISSIONS.tags.edit}
            >
              Save Edits
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default TagEditorPage;
